import React, { useEffect, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowTrendUp,
  faCashRegister,
  faHandHoldingDollar,
  faHashtag,
  faMotorcycle,
  faMoneyBillTransfer,
  faMoneyBillTrendUp,
  faMoneyCheckAlt,
  faReceipt,
} from "@fortawesome/free-solid-svg-icons"
import { DailyCalculate } from "../../Objects/Calculates/DailyCalculate"
import { ICashFlowShareSlice } from "../../Objects/Calculates/ICashFlowShareSlice"
import { ICashFlowTimelinePoint } from "../../Interfaces/ICashFlowTimelinePoint"
import GetSpace from "../../utils/space"
import { useGetOrder } from "../../hooks/Order/useGetOrder"
import { companyMemory, orderMemory } from "../../Objects/Memory/Memory"
import { convertToLocalMoney, fromInputDate } from "../../Objects/Generics/Helps"
import { useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { PopupHelpButton } from "../../components/Help/PopupHelpButton"
import { getStoreNow } from "../../utils/timeZone"
import { useManualExternalOrders } from "../../hooks/ManualExternalOrder/useManualExternalOrders"
import { ManualExternalOrderFinancialSummary } from "../../Objects/ManualExternalOrder/ManualExternalOrderFinancialSummary"
import { getManualExternalOrderSourceLabel } from "../../Objects/ManualExternalOrder/manualExternalOrderHelpers"
import { CashFlowPeriodFilterPopup } from "./CashFlowPeriodFilterPopup"
import { CashFlowMetricsLoading } from "./CashFlowMetricsLoading"
import {
  buildCompactLabelValueListHtml,
  buildCompactSectionHtml,
  buildLabelValueTableHtml,
  buildSummaryCardsHtml,
  openPrintReport,
} from "../../utils/printReport"
import {
  buildCashFlowMetricsCacheKey,
  deleteCashFlowMetricsCache,
  getCashFlowMetricsCache,
  setCashFlowMetricsCache,
} from "./CashFlowMetricsCache"
import { loadCashFlowOrders } from "./loadCashFlowOrders"
import { FinancialPageLayout } from "./components/FinancialPageLayout"
import { FinancialPageHeader } from "./components/FinancialPageHeader"
import { FinancialKpiCard as MetricCard } from "./components/FinancialKpiCard"
import { FinancialChartCard } from "./components/FinancialChartCard"
import { FinancialTimelineChart } from "./components/charts/FinancialTimelineChart"
import { FinancialDonutChart } from "./components/charts/FinancialDonutChart"
import { CashFlowPrintFormatPopup, type CashFlowPrintFormat } from "./CashFlowPrintFormatPopup"

interface DailyPageProps {
  canSelectDate: boolean
}

interface ChartCardProps {
  title: string
  subtitle: string
  data: ICashFlowTimelinePoint[]
  netKey: "net" | "gross"
  primaryLabel: string
  secondaryLabel: string
  emptyTitle: string
  helpDescription: string
  helpBullets: string[]
}

interface ShareCardProps {
  title: string
  subtitle: string
  data: ICashFlowShareSlice[]
  labels: Record<string, string>
  emptyTitle: string
  helpDescription: string
  helpBullets: string[]
}

interface CashFlowMetricsCacheValue {
  calculate: DailyCalculate
  manualExternalSummary: ManualExternalOrderFinancialSummary
  ordersCount: number
}

const formatCompactCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value)

const TimelineChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  data,
  netKey,
  primaryLabel,
  secondaryLabel,
  emptyTitle,
  helpDescription,
  helpBullets,
}) => {
  const timelineData = data.slice(-12).map((item) => ({
    label: item.label,
    value: item[netKey],
    secondaryValue: netKey === "net" ? item.gross : item.net,
  }))

  return (
    <FinancialChartCard
      title={title}
      subtitle={subtitle}
      help={{ helpKey: `pages.cashflow.timeline.${netKey}`, description: helpDescription, bullets: helpBullets }}
    >
      <FinancialTimelineChart
        data={timelineData}
        primaryLabel={primaryLabel}
        secondaryLabel={secondaryLabel}
        ariaLabel={title}
        emptyTitle={emptyTitle}
        emptyDescription={subtitle}
        formatValue={convertToLocalMoney}
        formatAxisValue={formatCompactCurrency}
      />
    </FinancialChartCard>
  )
}

const EMPTY_MANUAL_EXTERNAL_SUMMARY: ManualExternalOrderFinancialSummary = {
  count: 0,
  grossAmount: 0,
  netAmount: 0,
  feesAmount: 0,
  discountAmount: 0,
  costsAmount: 0,
  pendingReconciliationCount: 0,
  pendingReconciliationAmount: 0,
  receivedCount: 0,
  cancelledCount: 0,
  cancelledAmount: 0,
  sourceTotals: [],
}

const ShareChartCard: React.FC<ShareCardProps> = ({ title, subtitle, data, labels, emptyTitle, helpDescription, helpBullets }) => {
  const chartData = data.map((item) => ({ label: labels[item.label] ?? item.label, value: item.value }))
  return (
    <FinancialChartCard
      title={title}
      subtitle={subtitle}
      help={{ helpKey: `pages.cashflow.share.${title}`, description: helpDescription, bullets: helpBullets }}
    >
      <FinancialDonutChart
        data={chartData}
        ariaLabel={title}
        emptyTitle={emptyTitle}
        emptyDescription={subtitle}
        formatValue={convertToLocalMoney}
      />
    </FinancialChartCard>
  )
}

export const DailyPage: React.FC<DailyPageProps> = ({ canSelectDate }) => {
  const { t } = useTranslation("pages")

  const getHelpBullets = (key: string, options?: Record<string, unknown>) => {
    const translated = t(key, { returnObjects: true, ...(options ?? {}) })
    return Array.isArray(translated) ? translated.map((item) => String(item)) : []
  }

  const location = useLocation()

  const [today] = useState(() => getStoreNow(companyMemory.timeZoneId))

  const [dateStart, setDateStart] = useState(
    canSelectDate ? new Date(today.getFullYear(), today.getMonth(), 1).toInputDate() : today.toInputDate(),
  )
  const [dateFinal, setDateFinal] = useState(today.toInputDate())

  const [showPeriodFilter, setShowPeriodFilter] = useState(false)

  const [showPrintFormatPopup, setShowPrintFormatPopup] = useState(false)

  const { handleGetOrder } = useGetOrder()

  const { handleGetManualExternalOrderFinancialSummary } = useManualExternalOrders()

  const handleGetOrderRef = useRef(handleGetOrder)

  const handleGetManualExternalOrderFinancialSummaryRef = useRef(handleGetManualExternalOrderFinancialSummary)

  const [calculate, setCalculate] = useState<DailyCalculate>(new DailyCalculate())

  const [manualExternalSummary, setManualExternalSummary] =
    useState<ManualExternalOrderFinancialSummary>(EMPTY_MANUAL_EXTERNAL_SUMMARY)
  const [isMetricsLoading, setIsMetricsLoading] = useState(false)

  const [ordersCount, setOrdersCount] = useState(orderMemory.listData.length)

  const [refreshNonce, setRefreshNonce] = useState(0)

  useEffect(() => {
    handleGetOrderRef.current = handleGetOrder
    handleGetManualExternalOrderFinancialSummaryRef.current = handleGetManualExternalOrderFinancialSummary
  }, [handleGetManualExternalOrderFinancialSummary, handleGetOrder])

  useEffect(() => {
    let isMounted = true

    const runCalculate = async () => {
      const cacheKey = buildCashFlowMetricsCacheKey("cashflow", dateStart, dateFinal)

      const cachedMetrics = getCashFlowMetricsCache<CashFlowMetricsCacheValue>(cacheKey)

      if (cachedMetrics) {
        setCalculate(cachedMetrics.calculate)
        setManualExternalSummary(cachedMetrics.manualExternalSummary)
        setOrdersCount(cachedMetrics.ordersCount)
        return
      }

      setIsMetricsLoading(true)

      try {
        await loadCashFlowOrders(handleGetOrderRef.current, dateStart, dateFinal)

        let loadedManualExternalSummary = EMPTY_MANUAL_EXTERNAL_SUMMARY

        await handleGetManualExternalOrderFinancialSummaryRef.current(
          {
            dateStart,
            dateEnd: dateFinal,
          },
          (summary) => {
            loadedManualExternalSummary = summary

            if (isMounted) {
              setManualExternalSummary(summary)
            }
          },
        )

        if (!isMounted) {
          return
        }

        const nextCalculate = new DailyCalculate()

        nextCalculate.calculate()

        setCalculate(nextCalculate)

        setOrdersCount(orderMemory.listData.length)

        setCashFlowMetricsCache<CashFlowMetricsCacheValue>(cacheKey, {
          calculate: nextCalculate,
          manualExternalSummary: loadedManualExternalSummary,
          ordersCount: orderMemory.listData.length,
        })
      } finally {
        if (isMounted) {
          setIsMetricsLoading(false)
        }
      }
    }

    void runCalculate()

    return () => {
      isMounted = false
    }
  }, [canSelectDate, dateFinal, dateStart, location.pathname, refreshNonce])

  const paymentLabels = useMemo(
    () => ({
      cash: t("cashflow.payment_cash"),
      card: t("cashflow.payment_card"),
      pix: t("cashflow.payment_pix"),
      none: t("cashflow.payment_other"),
    }),
    [t],
  )

  const performanceLabels = useMemo(
    () => ({
      products: t("cashflow.performance_products"),
      catalogItems: t("cashflow.performance_catalogItems"),
      addtionals: t("cashflow.performance_additionals"),
      services: t("cashflow.performance_services"),
    }),
    [t],
  )

  const channelLabels = useMemo(
    () => ({
      local: t("cashflow.channel_local"),
      delivery: t("cashflow.channel_delivery"),
      ...Object.fromEntries(
        (manualExternalSummary.sourceTotals ?? [])
          .filter((item) => Number.isFinite(item.sourceType) && item.netAmount > 0)
          .map((item) => [`manualExternal:${item.sourceType}`, getManualExternalOrderSourceLabel(item.sourceType)]),
      ),
    }),
    [manualExternalSummary.sourceTotals, t],
  )

  const salesChannelShareWithManual = useMemo(
    () => [
      ...calculate.salesChannelShare,
      ...(manualExternalSummary.sourceTotals ?? [])
        .filter((item) => Number.isFinite(item.sourceType) && item.netAmount > 0)
        .map((item) => ({
          label: `manualExternal:${item.sourceType}`,
          value: item.netAmount,
        })),
    ],
    [calculate.salesChannelShare, manualExternalSummary.sourceTotals],
  )

  const totalOrdersWithManual = ordersCount + manualExternalSummary.count + manualExternalSummary.cancelledCount

  const totalClosedWithManual = calculate.countClosed + manualExternalSummary.receivedCount

  const totalOpenAmountWithManual = calculate.valueTotalDailyOpen + manualExternalSummary.pendingReconciliationAmount

  const totalCanceledAmountWithManual = calculate.valueTotalCanceledDaily + manualExternalSummary.cancelledAmount

  const totalGrossWithManual =
    calculate.valueTotalGross + manualExternalSummary.grossAmount + manualExternalSummary.cancelledAmount

  const totalAverageTicketWithManual =
    totalOrdersWithManual > 0 ? totalGrossWithManual / totalOrdersWithManual : calculate.averageTicket

  const handleApplyPeriodFilter = (filters: { dateStart: string; dateEnd: string }, forceRefresh?: boolean) => {
    if (forceRefresh) {
      deleteCashFlowMetricsCache(buildCashFlowMetricsCacheKey("cashflow", filters.dateStart, filters.dateEnd))

      if (filters.dateStart === dateStart && filters.dateEnd === dateFinal) {
        setRefreshNonce((currentValue) => currentValue + 1)
      }
    }

    setDateStart(filters.dateStart)
    setDateFinal(filters.dateEnd)
    setShowPeriodFilter(false)
  }

  const handlePrintReport = (format: CashFlowPrintFormat) => {
    const columnLabels = {
      labelHeader: t("cashflow.print_report.label_column"),
      valueHeader: t("cashflow.print_report.value_column"),
      emptyLabel: t("cashflow.print_report.empty_state"),
    }

    const totalOrdersRow = {
      label: t("cashflow.total_orders"),
      value: totalOrdersWithManual.toString(),
      subtitle: `${t("manualExternalOrder:cashflow.manual_sales")}: ${manualExternalSummary.count}`,
    }

    const totalReceivedRow = {
      label: t("cashflow.total_received"),
      value: convertToLocalMoney(calculate.valueTotalDaily + manualExternalSummary.netAmount),
      subtitle: `${t("cashflow.closed_count")}: ${totalClosedWithManual}`,
    }

    const openOrdersRow = {
      label: t("cashflow.open_orders"),
      value: convertToLocalMoney(totalOpenAmountWithManual),
      subtitle: `${t("cashflow.open_count")}: ${calculate.countOpen}`,
    }

    const canceledOrdersRow = {
      label: t("cashflow.canceled"),
      value: convertToLocalMoney(totalCanceledAmountWithManual),
      subtitle: `${t("cashflow.canceled_count")}: ${calculate.countCanceled + manualExternalSummary.cancelledCount}`,
    }

    const grossRevenueRow = {
      label: t("cashflow.gross_revenue"),
      value: convertToLocalMoney(totalGrossWithManual),
      subtitle: `${t("cashflow.average_ticket")}: ${convertToLocalMoney(totalAverageTicketWithManual)}`,
    }

    const commissionEstimateRow = {
      label: t("cashflow.commission_estimate"),
      value: convertToLocalMoney(calculate.totalCommissionEstimate),
    }

    const manualNetRow = {
      label: t("manualExternalOrder:cashflow.manual_net"),
      value: convertToLocalMoney(manualExternalSummary.netAmount),
    }

    const conversionRow = {
      label: t("cashflow.conversion"),
      value: `${calculate.closureRate.toFixed(1)}%`,
      subtitle: `${t("cashflow.cancel_rate")}: ${calculate.cancelRate.toFixed(1)}%`,
    }

    const summaryCards = [
      totalOrdersRow,
      totalReceivedRow,
      { label: openOrdersRow.label, value: openOrdersRow.value },
      { label: canceledOrdersRow.label, value: canceledOrdersRow.value },
      grossRevenueRow,
      commissionEstimateRow,
      manualNetRow,
      conversionRow,
    ]

    const executiveRows = [
      { label: t("cashflow.performance_products"), value: convertToLocalMoney(calculate.totalProducts) },
      { label: t("cashflow.performance_catalogItems"), value: convertToLocalMoney(calculate.totalCatalogItems) },
      { label: t("cashflow.performance_additionals"), value: convertToLocalMoney(calculate.totalAddtionals) },
      { label: t("cashflow.performance_services"), value: convertToLocalMoney(calculate.totalExtraServices) },
      { label: t("cashflow.awards_total"), value: convertToLocalMoney(calculate.totalAwards) },
      { label: t("manualExternalOrder:cashflow.manual_net"), value: convertToLocalMoney(manualExternalSummary.netAmount) },
      { label: t("manualExternalOrder:cashflow.manual_gross"), value: convertToLocalMoney(manualExternalSummary.grossAmount) },
      { label: t("manualExternalOrder:cashflow.manual_fees"), value: convertToLocalMoney(manualExternalSummary.feesAmount) },
      { label: t("manualExternalOrder:cashflow.manual_discount"), value: convertToLocalMoney(manualExternalSummary.discountAmount) },
      { label: t("manualExternalOrder:cashflow.manual_costs"), value: convertToLocalMoney(manualExternalSummary.costsAmount) },
    ]

    const buildShareRows = (data: ICashFlowShareSlice[], labels: Record<string, string>) =>
      data.map((item) => ({ label: labels[item.label] ?? item.label, value: convertToLocalMoney(item.value) }))

    const buildTimelineRows = (data: ICashFlowTimelinePoint[], key: "net" | "gross") =>
      data.map((item) => ({ label: item.label, value: convertToLocalMoney(item[key]) }))

    const thermalRows = [
      totalOrdersRow,
      totalReceivedRow,
      openOrdersRow,
      canceledOrdersRow,
      grossRevenueRow,
      commissionEstimateRow,
      manualNetRow,
      conversionRow,
    ]

    const bodyHtml =
      format === "thermal"
        ? [
            buildCompactLabelValueListHtml(thermalRows),
            buildCompactSectionHtml(t("cashflow.executive_title"), executiveRows),
          ].join("")
        : [
            buildSummaryCardsHtml(summaryCards),
            buildLabelValueTableHtml(t("cashflow.executive_title"), executiveRows, columnLabels),
            buildLabelValueTableHtml(
              t("cashflow.hourly_chart_title"),
              buildTimelineRows(calculate.hourlySales, "net"),
              columnLabels,
            ),
            buildLabelValueTableHtml(
              t("cashflow.daily_chart_title"),
              buildTimelineRows(calculate.weeklySales, "gross"),
              columnLabels,
            ),
            buildLabelValueTableHtml(
              t("cashflow.payment_chart_title"),
              buildShareRows(calculate.paymentShare, paymentLabels),
              columnLabels,
            ),
            buildLabelValueTableHtml(
              t("cashflow.performance_chart_title"),
              buildShareRows(calculate.productPerformanceShare, performanceLabels),
              columnLabels,
            ),
            buildLabelValueTableHtml(
              t("cashflow.channel_chart_title"),
              buildShareRows(salesChannelShareWithManual, channelLabels),
              columnLabels,
            ),
          ].join("")

    openPrintReport({
      title: canSelectDate ? t("label_moth_money") : t("label_today_money"),
      subtitle: [
        companyMemory.companyName || t("cashflow.print_report.store_fallback"),
        t("cashflow.print_report.period_label", {
          startDate: fromInputDate(dateStart).toLocaleDateString("pt-BR"),
          endDate: fromInputDate(dateFinal).toLocaleDateString("pt-BR"),
        }),
        t("cashflow.print_report.generated_at_label", {
          dateTime: getStoreNow(companyMemory.timeZoneId).toLocaleDateTimeOrDash(
            "pt-BR",
            companyMemory.timeZoneId,
          ),
        }),
      ].join(" • "),
      bodyHtml,
      format,
    })
  }

  const handleSelectPrintFormat = (format: CashFlowPrintFormat) => {
    setShowPrintFormatPopup(false)
    handlePrintReport(format)
  }

  return (
    <FinancialPageLayout>
      {showPeriodFilter && (
        <CashFlowPeriodFilterPopup
          initialFilters={{ dateStart, dateEnd: dateFinal }}
          today={today}
          onApplyFilters={handleApplyPeriodFilter}
          onClose={() => setShowPeriodFilter(false)}
        />
      )}

      {showPrintFormatPopup && (
        <CashFlowPrintFormatPopup
          onSelectFormat={handleSelectPrintFormat}
          onClose={() => setShowPrintFormatPopup(false)}
        />
      )}

      <FinancialPageHeader
        title={canSelectDate ? t("label_moth_money") : t("label_today_money")}
        subtitle={canSelectDate ? t("cashflow.period_page_subtitle") : t("cashflow.today_page_subtitle")}
        period={t("cashflow.print_report.period_label", {
          startDate: fromInputDate(dateStart).toLocaleDateString("pt-BR"),
          endDate: fromInputDate(dateFinal).toLocaleDateString("pt-BR"),
        })}
        filterLabel={t("cashflow.filter_action")}
        printLabel={t("cashflow.print_action")}
        isLoading={isMetricsLoading}
        onFilter={() => setShowPeriodFilter(true)}
        onPrint={() => setShowPrintFormatPopup(true)}
      />

      <div>
        <CashFlowMetricsLoading isVisible={isMetricsLoading} />

        {!isMetricsLoading && (
          <>
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
              <MetricCard
                title={t("cashflow.total_orders")}
                value={totalOrdersWithManual.toString()}
                subtitle={`${t("manualExternalOrder:cashflow.manual_sales")}: ${manualExternalSummary.count}`}
                icon={faHashtag}
                helpDescription={t("cashflow.help.total_orders.description")}
                helpBullets={getHelpBullets("cashflow.help.total_orders.bullets")}
              />

              <MetricCard
                title={t("cashflow.total_received")}
                value={convertToLocalMoney(calculate.valueTotalDaily + manualExternalSummary.netAmount)}
                subtitle={`${t("cashflow.closed_count")}: ${totalClosedWithManual} | ${t("manualExternalOrder:cashflow.manual_net")}: ${convertToLocalMoney(manualExternalSummary.netAmount)}`}
                icon={faMoneyBillTransfer}
                helpDescription={t("cashflow.help.total_received.description")}
                helpBullets={getHelpBullets("cashflow.help.total_received.bullets", { count: calculate.countClosed })}
              />

              <MetricCard
                title={t("cashflow.open_orders")}
                value={convertToLocalMoney(totalOpenAmountWithManual)}
                subtitle={`${t("cashflow.open_count")}: ${calculate.countOpen} | ${t("manualExternalOrder:cashflow.manual_pending")}: ${manualExternalSummary.pendingReconciliationCount}`}
                icon={faMoneyBillTrendUp}
                helpDescription={t("cashflow.help.open_orders.description")}
                helpBullets={getHelpBullets("cashflow.help.open_orders.bullets", { count: calculate.countOpen })}
              />

              <MetricCard
                title={t("cashflow.canceled")}
                value={convertToLocalMoney(totalCanceledAmountWithManual)}
                subtitle={`${t("cashflow.canceled_count")}: ${calculate.countCanceled + manualExternalSummary.cancelledCount}`}
                tone="negative"
                icon={faMoneyCheckAlt}
                helpDescription={t("cashflow.help.canceled.description")}
                helpBullets={getHelpBullets("cashflow.help.canceled.bullets", { count: calculate.countCanceled })}
              />

              <MetricCard
                title={t("cashflow.gross_revenue")}
                value={convertToLocalMoney(totalGrossWithManual)}
                subtitle={`${t("cashflow.average_ticket")}: ${convertToLocalMoney(totalAverageTicketWithManual)}`}
                icon={faCashRegister}
                helpDescription={t("cashflow.help.gross_revenue.description")}
                helpBullets={getHelpBullets("cashflow.help.gross_revenue.bullets")}
              />

              <MetricCard
                title={t("cashflow.commission_estimate")}
                value={convertToLocalMoney(calculate.totalCommissionEstimate)}
                subtitle={`${t("cashflow.discount_total")}: ${convertToLocalMoney(calculate.totalDiscount + manualExternalSummary.discountAmount)} | ${t("manualExternalOrder:cashflow.manual_fees")}: ${convertToLocalMoney(manualExternalSummary.feesAmount)}`}
                icon={faHandHoldingDollar}
                helpDescription={t("cashflow.help.commission_estimate.description")}
                helpBullets={getHelpBullets("cashflow.help.commission_estimate.bullets")}
              />

              <MetricCard
                title={t("manualExternalOrder:cashflow.manual_net")}
                value={convertToLocalMoney(manualExternalSummary.netAmount)}
                subtitle={`${t("manualExternalOrder:cashflow.manual_gross")}: ${convertToLocalMoney(manualExternalSummary.grossAmount)}`}
                icon={faReceipt}
                helpDescription={t("manualExternalOrder:popup.subtitle")}
                helpBullets={[]}
              />

              <MetricCard
                title={t("manualExternalOrder:cashflow.manual_pending")}
                value={convertToLocalMoney(manualExternalSummary.pendingReconciliationAmount)}
                subtitle={`${manualExternalSummary.pendingReconciliationCount} ${t("manualExternalOrder:cashflow.manual_pending").toLowerCase()}`}
                tone={manualExternalSummary.pendingReconciliationCount > 0 ? "warning" : "neutral"}
                icon={faMoneyCheckAlt}
                helpDescription={t("manualExternalOrder:list.backend_totals")}
                helpBullets={[]}
              />

              <MetricCard
                title={t("manualExternalOrder:cashflow.manual_fees")}
                value={convertToLocalMoney(manualExternalSummary.feesAmount)}
                subtitle={`${t("manualExternalOrder:cashflow.manual_costs")}: ${convertToLocalMoney(manualExternalSummary.costsAmount)} | ${t("manualExternalOrder:cashflow.manual_discount")}: ${convertToLocalMoney(manualExternalSummary.discountAmount)}`}
                icon={faHandHoldingDollar}
                helpDescription={t("manualExternalOrder:list.backend_totals")}
                helpBullets={[]}
              />

              <MetricCard
                title={t("manualExternalOrder:cashflow.manual_cancelled")}
                value={convertToLocalMoney(manualExternalSummary.cancelledAmount)}
                subtitle={`${t("cashflow.canceled_count")}: ${manualExternalSummary.cancelledCount}`}
                tone={manualExternalSummary.cancelledCount > 0 ? "negative" : "neutral"}
                icon={faMoneyCheckAlt}
                helpDescription={t("manualExternalOrder:list.backend_totals")}
                helpBullets={[]}
              />

              <MetricCard
                title={t("cashflow.delivery_vs_local")}
                value={`${calculate.deliveryOrders} / ${calculate.serviceOrders}`}
                subtitle={t("cashflow.delivery_vs_local_subtitle")}
                icon={faMotorcycle}
                helpDescription={t("cashflow.help.delivery_vs_local.description")}
                helpBullets={getHelpBullets("cashflow.help.delivery_vs_local.bullets", {
                  delivery: calculate.deliveryOrders,
                  service: calculate.serviceOrders,
                })}
              />

              <MetricCard
                title={t("cashflow.conversion")}
                value={`${calculate.closureRate.toFixed(1)}%`}
                subtitle={`${t("cashflow.cancel_rate")}: ${calculate.cancelRate.toFixed(1)}%`}
                icon={faArrowTrendUp}
                helpDescription={t("cashflow.help.conversion.description")}
                helpBullets={getHelpBullets("cashflow.help.conversion.bullets")}
              />
            </div>

            <GetSpace top={10} />

            <Animate type={AnimationTypes.fadeInTraslate}>
              <div className="grid w-full grid-cols-1 gap-4 xl:grid-cols-2">
                <TimelineChartCard
                  title={t("cashflow.hourly_chart_title")}
                  subtitle={t("cashflow.hourly_chart_subtitle")}
                  data={calculate.hourlySales}
                  netKey="net"
                  primaryLabel={t("cashflow.net_series")}
                  secondaryLabel={t("cashflow.gross_series")}
                  emptyTitle={t("cashflow.chart_empty_title")}
                  helpDescription={t("cashflow.help.hourly_chart.description")}
                  helpBullets={getHelpBullets("cashflow.help.hourly_chart.bullets")}
                />

                <TimelineChartCard
                  title={t("cashflow.daily_chart_title")}
                  subtitle={t("cashflow.daily_chart_subtitle")}
                  data={calculate.weeklySales}
                  netKey="gross"
                  primaryLabel={t("cashflow.gross_series")}
                  secondaryLabel={t("cashflow.net_series")}
                  emptyTitle={t("cashflow.chart_empty_title")}
                  helpDescription={t("cashflow.help.daily_chart.description")}
                  helpBullets={getHelpBullets("cashflow.help.daily_chart.bullets")}
                />

                <ShareChartCard
                  title={t("cashflow.payment_chart_title")}
                  subtitle={t("cashflow.payment_chart_subtitle")}
                  data={calculate.paymentShare}
                  labels={paymentLabels}
                  emptyTitle={t("cashflow.chart_empty_title")}
                  helpDescription={t("cashflow.help.payment_chart.description")}
                  helpBullets={getHelpBullets("cashflow.help.payment_chart.bullets")}
                />

                <ShareChartCard
                  title={t("cashflow.performance_chart_title")}
                  subtitle={t("cashflow.performance_chart_subtitle")}
                  data={calculate.productPerformanceShare}
                  labels={performanceLabels}
                  emptyTitle={t("cashflow.chart_empty_title")}
                  helpDescription={t("cashflow.help.performance_chart.description")}
                  helpBullets={getHelpBullets("cashflow.help.performance_chart.bullets")}
                />

                <ShareChartCard
                  title={t("cashflow.channel_chart_title")}
                  subtitle={t("cashflow.channel_chart_subtitle")}
                  data={salesChannelShareWithManual}
                  labels={channelLabels}
                  emptyTitle={t("cashflow.chart_empty_title")}
                  helpDescription={t("cashflow.help.channel_chart.description")}
                  helpBullets={getHelpBullets("cashflow.help.channel_chart.bullets")}
                />

                <div className="relative overflow-hidden rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/30 to-white p-4 shadow-sm ">
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300" />

                  <div className="mb-1 flex items-center gap-2 text-sm font-bold justify-between uppercase tracking-wide text-slate-700">
                    <div className="flex items-center gap-2">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-orange-200 bg-white text-orange-500 shadow-sm">
                        <FontAwesomeIcon icon={faCashRegister} />
                      </span>
                      <h3>{t("cashflow.executive_title")}</h3>
                    </div>

                    <PopupHelpButton
                      helpKey="pages.cashflow.cashflow.3"
                      title={t("cashflow.executive_title")}
                      description={t("cashflow.help.executive.description")}
                      bullets={getHelpBullets("cashflow.help.executive.bullets")}
                    />
                  </div>

                  <p className="mt-1 text-xs text-slate-500">{t("cashflow.executive_subtitle")}</p>

                  <div className="mt-4 grid gap-2">
                    <div className="flex items-center justify-between rounded-lg bg-orange-50 px-3 py-2">
                      <span className="text-xs font-semibold text-slate-600">{t("cashflow.performance_products")}</span>

                      <span className="text-xs font-bold text-slate-800">
                        {convertToLocalMoney(calculate.totalProducts)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between rounded-lg bg-orange-50 px-3 py-2">
                      <span className="text-xs font-semibold text-slate-600">{t("cashflow.performance_catalogItems")}</span>

                      <span className="text-xs font-bold text-slate-800">
                        {convertToLocalMoney(calculate.totalCatalogItems)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between rounded-lg bg-orange-50 px-3 py-2">
                      <span className="text-xs font-semibold text-slate-600">
                        {t("cashflow.performance_additionals")}
                      </span>

                      <span className="text-xs font-bold text-slate-800">
                        {convertToLocalMoney(calculate.totalAddtionals)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between rounded-lg bg-orange-50 px-3 py-2">
                      <span className="text-xs font-semibold text-slate-600">{t("cashflow.performance_services")}</span>

                      <span className="text-xs font-bold text-slate-800">
                        {convertToLocalMoney(calculate.totalExtraServices)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between rounded-lg bg-slate-100 px-3 py-2">
                      <span className="text-xs font-semibold text-slate-600">{t("cashflow.awards_total")}</span>

                      <span className="text-xs font-bold text-slate-800">
                        {convertToLocalMoney(calculate.totalAwards)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between rounded-lg bg-emerald-50 px-3 py-2">
                      <span className="text-xs font-semibold text-slate-600">
                        {t("manualExternalOrder:cashflow.manual_net")}
                      </span>

                      <span className="text-xs font-bold text-slate-800">
                        {convertToLocalMoney(manualExternalSummary.netAmount)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between rounded-lg bg-orange-50 px-3 py-2">
                      <span className="text-xs font-semibold text-slate-600">
                        {t("manualExternalOrder:cashflow.manual_gross")}
                      </span>

                      <span className="text-xs font-bold text-slate-800">
                        {convertToLocalMoney(manualExternalSummary.grossAmount)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between rounded-lg bg-red-50 px-3 py-2">
                      <span className="text-xs font-semibold text-slate-600">
                        {t("manualExternalOrder:cashflow.manual_fees")}
                      </span>

                      <span className="text-xs font-bold text-slate-800">
                        {convertToLocalMoney(manualExternalSummary.feesAmount)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between rounded-lg bg-red-50 px-3 py-2">
                      <span className="text-xs font-semibold text-slate-600">
                        {t("manualExternalOrder:cashflow.manual_discount")}
                      </span>

                      <span className="text-xs font-bold text-slate-800">
                        {convertToLocalMoney(manualExternalSummary.discountAmount)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between rounded-lg bg-slate-100 px-3 py-2">
                      <span className="text-xs font-semibold text-slate-600">
                        {t("manualExternalOrder:cashflow.manual_costs")}
                      </span>

                      <span className="text-xs font-bold text-slate-800">
                        {convertToLocalMoney(manualExternalSummary.costsAmount)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3 text-xs">
                    <span className="font-semibold text-slate-500">{t("cashflow.health_status")}</span>

                    <span className="rounded-full bg-green-100 px-2 py-1 font-bold text-green-700">
                      {t("cashflow.health_positive")}
                    </span>
                  </div>
                </div>
              </div>
            </Animate>
          </>
        )}
      </div>
    </FinancialPageLayout>
  )
}
